/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */
import React from 'react';

import { useTabManagerContext, DEBUG_TAB_MODIFICATION } from '../context/TabManagerContext';
import { findTabById, Tab } from '../helpers/codeEditorHelpers';
import { AstParser } from '../managers/AstParser';

import { CachedTab } from './types';

import { toast } from '@/components/ui/use-toast';
import { Step as UIStep, Story } from '@/pages/projects/LiveAgentTypes';

export const useTabOperations = () => {
    const context = useTabManagerContext();
    const {
        editorRef,
        activeTabIdRef,
        tabContentRef,
        tabOriginalContentRef,
        tabsRef,
        setTabs,
        updateActiveTabId,
        updateTabsRef,
        blocksData,
    } = context;

    /**
     * Helper function to get block-specific steps by block name
     * @param blockName - Name of the block (without 'block-' prefix)
     * @param getTabInfo - Function to get tab info
     * @param getCachedTab - Function to get cached tab data
     * @returns Block-specific steps for the given block name
     */
    const getBlockStepsByName = (
        blockName: string,
        getTabInfo?: (tabKey: string, getCachedTab: (key: string) => CachedTab | undefined) => { steps: UIStep[] },
        getCachedTab?: (key: string) => CachedTab | undefined,
    ): UIStep[] => {
        if (getTabInfo && getCachedTab) {
            // Use getTabInfo to get block-specific steps
            return getTabInfo(blockName, getCachedTab).steps;
        } else if (getCachedTab) {
            // Fallback: get cached steps for this specific block
            const existingCache = getCachedTab(blockName);
            return existingCache?.steps || [];
        }
        return [];
    };

    /**
     * Core tab switching logic extracted to avoid duplication
     * @param targetTabId - The tab ID to switch to
     * @param blockSteps - Block steps to pass to applyTabContent (empty array for recursive calls)
     * @param dependencies - All the dependency functions needed
     */
    const performTabSwitch = (
        targetTabId: string,
        blockSteps: UIStep[],
        dependencies: {
            cacheCurrentTab: () => void;
            clearAllDecorations: () => void;
            applyTabContent: (
                currentTabId: string,
                tabSteps: UIStep[],
                currentTabContentRef: React.MutableRefObject<Map<string, string>>,
                currentTabsRef: React.MutableRefObject<Tab[]>,
                inputBlocksData: Story[],
            ) => void;
            updateCurrentTabModificationStatus: () => void;
            updateContextMenuActions?: () => void;
            dispatchBlockSteps?: (blockName: string, inputBlockSteps: UIStep[]) => boolean;
            getTabInfo?: (tabKey: string, getCachedTab: (key: string) => CachedTab | undefined) => { steps: UIStep[] };
            getCachedTab?: (key: string) => CachedTab | undefined;
        },
    ) => {
        const {
            cacheCurrentTab,
            clearAllDecorations,
            applyTabContent,
            updateCurrentTabModificationStatus,
            updateContextMenuActions,
            dispatchBlockSteps,
            getTabInfo,
            getCachedTab,
        } = dependencies;

        const isBlockTab = activeTabIdRef.current.startsWith('block-');

        // Cache current tab before switching
        cacheCurrentTab();

        // Dispatch block steps if switching away from a block tab
        // const activeTab = tabsRef.current.find((tab) => tab.id === activeTabIdRef.current);
        if (isBlockTab && dispatchBlockSteps) {
            const blockName = activeTabIdRef.current.substring(6); // Remove 'block-' prefix
            const currentBlockSteps = getBlockStepsByName(blockName, getTabInfo, getCachedTab);
            const success = dispatchBlockSteps(blockName, currentBlockSteps);
            if (!success) {
                toast({
                    title: 'Error dispatching block steps',
                    description: `Failed to dispatch block steps for ${blockName}`,
                    variant: 'destructive',
                });
                return;
            }
        }

        // Clear all decorations
        clearAllDecorations();

        // Apply tab content
        applyTabContent(targetTabId, blockSteps, tabContentRef, tabsRef, blocksData || []);

        updateActiveTabId(targetTabId);

        // Update context menu actions for the new tab
        setTimeout(() => {
            updateCurrentTabModificationStatus();
            updateContextMenuActions?.();
        }, 0);
    };

    const switchTab = (
        tabId: string,
        astParser: AstParser,
        checkForBlockRename: (
            currentTabId: string,
            cacheCurrentTab: () => void,
            switchTabFn: (inputTabId: string) => void,
        ) => boolean,
        cacheCurrentTab: () => void,
        clearAllDecorations: () => void,
        applyTabContent: (
            currentTabId: string,
            tabSteps: UIStep[],
            currentTabContentRef: React.MutableRefObject<Map<string, string>>,
            currentTabsRef: React.MutableRefObject<Tab[]>,
            inputBlocksData: Story[],
        ) => void,
        updateCurrentTabModificationStatus: () => void,
        updateContextMenuActions?: () => void,
        dispatchBlockSteps?: (blockName: string, blockSteps: UIStep[]) => boolean,
        getTabInfo?: (tabKey: string, getCachedTab: (key: string) => CachedTab | undefined) => { steps: UIStep[] },
        getCachedTab?: (key: string) => CachedTab | undefined,
    ) => {
        if (editorRef.current && activeTabIdRef.current !== tabId) {
            const dependencies = {
                cacheCurrentTab,
                clearAllDecorations,
                applyTabContent,
                updateCurrentTabModificationStatus,
                updateContextMenuActions,
                dispatchBlockSteps,
                getTabInfo,
                getCachedTab,
            };

            // Check for block rename - create a recursive-safe switchTab function
            const switchTabRecursive = (recursiveTabId: string) => {
                // For recursive calls, we pass empty steps as metadata should already be populated
                performTabSwitch(recursiveTabId, [], dependencies);
            };

            // Check for block rename
            if (checkForBlockRename(tabId, cacheCurrentTab, switchTabRecursive)) {
                return; // Tab switch already handled by rename logic
            }

            // Validate the tab content
            const { isValid } = astParser.validateSyntax(tabContentRef.current.get(activeTabIdRef.current) || '');
            if (!isValid) {
                toast({
                    title: 'Cannot switch tabs',
                    description: `Fix the syntax errors before switching tabs`,
                    variant: 'destructive',
                });
                return;
            }

            // Get block steps for regular tab switches
            const isBlockTab = activeTabIdRef.current.startsWith('block-');
            let blockSteps: UIStep[] = [];
            if (isBlockTab && dispatchBlockSteps) {
                const blockName = activeTabIdRef.current.substring(6); // Remove 'block-' prefix
                blockSteps = getBlockStepsByName(blockName, getTabInfo, getCachedTab);
            }

            // Perform the actual tab switch
            performTabSwitch(tabId, blockSteps, dependencies);
            return;
        }

        updateActiveTabId(tabId);

        // Update context menu actions for the new tab
        setTimeout(() => {
            updateCurrentTabModificationStatus();
            updateContextMenuActions?.();
        }, 0);
    };

    const closeTab = (
        tabId: string,
        astParser: AstParser,
        cacheCurrentTab: () => void,
        getCachedTab: (key: string) => CachedTab | undefined,
        setCachedTab: (key: string, cachedTab: CachedTab) => void,
        dispatchBlockSteps: (blockName: string, blockSteps: UIStep[]) => boolean,
        applyTabContent: (
            currentTabId: string,
            tabSteps: UIStep[],
            currentTabContentRef: React.MutableRefObject<Map<string, string>>,
            currentTabsRef: React.MutableRefObject<Tab[]>,
            inputBlocksData: Story[],
        ) => void,
        getTabInfo?: (tabKey: string, getCachedTabFn: (key: string) => CachedTab | undefined) => { steps: UIStep[] },
    ) => {
        // Don't close main tab
        if (tabId === 'main') return;

        // Validate the tab content
        const { isValid } = astParser.validateSyntax(tabContentRef.current.get(tabId) || '');
        if (!isValid) {
            toast({
                title: 'Cannot close tab',
                description: `Fix the syntax errors before closing the tab`,
                variant: 'destructive',
            });
            return;
        }

        // Save steps
        const tabToClose = findTabById(tabsRef.current, tabId);
        if (!tabToClose || !editorRef.current || !tabToClose.metadataManager) return;

        // Save current content before closing
        cacheCurrentTab();

        // Extract block name from tab ID (e.g., 'block-login' -> 'login')
        const blockName = tabId.startsWith('block-') ? tabId.substring(6) : tabId;

        // Get the correct steps for this specific block tab
        const blockSteps = getBlockStepsByName(blockName, getTabInfo, getCachedTab);

        // Cache the block information
        const currentCode = tabContentRef.current.get(tabId) || '';

        // Determine if the block has been modified
        const existingCache = getCachedTab(blockName);
        const originalContent = tabOriginalContentRef.current.get(tabId) || '';
        const isModified = currentCode !== originalContent || existingCache?.isModified || false;

        if (DEBUG_TAB_MODIFICATION) {
            // eslint-disable-next-line no-console
            console.log(`[TAB_MODIFICATION] Setting cached tab isModified = ${isModified} when closing tab`, {
                tabId,
                blockName,
                isModified,
                reason: isModified
                    ? 'code differs from original or existing cache was modified'
                    : 'code matches original and no existing modifications',
                location: 'useTabOperations.closeTab',
                codeLength: currentCode.length,
                originalContentLength: originalContent.length,
                hadExistingCache: !!existingCache,
                existingCacheModified: existingCache?.isModified || false,
            });
        }

        setCachedTab(blockName, {
            code: currentCode,
            originalCode: originalContent,
            steps: blockSteps,
            metadataManager: tabToClose.metadataManager,
            decorationManager: tabToClose.decorationManager,
            isModified,
        });

        // Set the blockSteps in other tabs
        dispatchBlockSteps(blockName, blockSteps);

        if (tabToClose?.decorationManager) {
            tabToClose.decorationManager.clearDecorations();
        }

        // Remove from content ref
        tabContentRef.current.delete(tabId);
        tabOriginalContentRef.current.delete(tabId);

        setTabs((prevTabs) => {
            const newTabs = prevTabs.filter((tab) => tab.id !== tabId);
            updateTabsRef(newTabs);
            return newTabs;
        });

        if (activeTabIdRef.current === tabId) {
            applyTabContent('main', [], tabContentRef, tabsRef, blocksData || []);
            updateActiveTabId('main');
        }

        // Trigger re-render for tab bar
        if (context.triggerRerender) {
            context.triggerRerender();
        }
    };

    return {
        switchTab,
        closeTab,
    };
};
